import { Navigation } from '@/components';
import {
  Box,
  Flex,
  HomeIcon,
  useToggle,
  LiquidationIcon,
  CoinsStackIcon,
  WalletIcon,
  MarketIcon,
  CollapsableSidebar,
  FlexProps,
  BoxProps,
  CrossIcon,
  CryptoIcon
} from '@blockanalitica/ui';
import { NavigationRoute } from '../Navigation';
import { Link, Outlet, useLocation } from 'react-router';
import {
  default as Logo
  // @ts-ignore
} from '@/assets/logos/compound.svg?react';

import { ReactNode, useEffect, useMemo } from 'react';
import { styled, useTheme } from 'styled-components';
import { useNetwork } from '@/hooks';
import { useParams } from 'react-router';
import { titleCase } from '@/utils';
import Footer from './Footer';

const Wrapper = styled(Flex)`
  box-sizing: border-box;
`;

const ContentWrapper = styled(Flex)`
  box-sizing: border-box;
  padding: ${(props) =>
    `${props.theme.space['xs']} ${props.theme.space['s-m']} ${props.theme.space['s-m']}`};
`;

const AlphaBackgroundLayer = styled.div`
  display: block;
  position: fixed;
  inset: 0;
  background-color: ${(props) => props.theme.colors.black23};
  z-index: 49;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
`;

const SidebarButton = styled(Flex)<FlexProps>`
  cursor: pointer;

  &:hover {
    color: ${(props) => props.theme.colors.linkHover};
  }
`;

const CloseButton = styled(Box)<BoxProps>`
  cursor: pointer;

  &:hover {
    color: ${(props) => props.theme.colors.linkHover};
  }
`;

type LayoutProps = {
  children?: ReactNode;
};

export default function Layout({ children }: LayoutProps) {
  const location = useLocation();
  const network = useNetwork() as string;
  const { marketAddress } = useParams();
  const theme = useTheme();

  const [sidebarOpen, toggleSidebar] = useToggle();

  useEffect(() => {
    toggleSidebar(false);
  }, [location.pathname, toggleSidebar]);

  const navigationRoutes: NavigationRoute[] = useMemo(() => {
    return [
      {
        title: 'Networks',
        url: '/',
        icon: <HomeIcon size="1" color={theme.colors.muted} />
      },
      {
        title: titleCase(network),
        url: `/${network}`,
        icon: <CryptoIcon name={`${network}_bw`} size="1" />,
        end: true
      },
      {
        title: 'Suppliers',
        url: `/${network}/${marketAddress}/suppliers`,
        icon: <CoinsStackIcon size="1" color={theme.colors.muted} />
      },
      {
        title: 'Borrowers',
        url: `/${network}/${marketAddress}/borrowers`,
        icon: <WalletIcon size="1" color={theme.colors.muted} />
      },
      {
        title: 'Liquidations',
        url: `/${network}/${marketAddress}/liquidations`,
        icon: <LiquidationIcon size="1" color={theme.colors.muted} />
      }
    ];
  }, [network, marketAddress, theme.colors]);

  return (
    <>
      <Wrapper height="100%" flexWrap="nowrap" width="100%" gap="0">
        <CollapsableSidebar
          sidebarOpen={sidebarOpen}
          toggleSidebar={toggleSidebar}
          alignItems="flex-start">
          <Flex justifyContent="space-between" width="100%">
            <Logo height={27} />

            <CloseButton
              alignSelf="center"
              onClick={() => toggleSidebar(false)}>
              <CrossIcon size={14} />
            </CloseButton>
          </Flex>

          <Navigation
            routes={navigationRoutes}
            flexDirection="column"
            alignItems="flex-start"
            fontSize="2"
            gap="m"
          />
        </CollapsableSidebar>

        <ContentWrapper
          flexGrow={1}
          flexShrink={1}
          flexBasis={['100%', 'auto']}
          margin={'0 auto'}
          width="100%"
          maxWidth={['100vw', '80rem']}
          flexDirection="column"
          justifyContent="space-between"
          gap="s">
          <Flex justifyContent="space-between" alignItems="center">
            <Box
              // @ts-ignore
              as={Link}
              to="/">
              <Logo height={27} />
            </Box>

            <Flex>
              <Box
                gap="xs"
                // @ts-ignore
                display={['none', 'flex']}>
                <Navigation routes={navigationRoutes} />
              </Box>

              <SidebarButton // @ts-ignore
                display={['flex', 'none']}>
                <MarketIcon
                  role="button"
                  onClick={() => toggleSidebar()}
                  size={20}
                />
              </SidebarButton>
            </Flex>
          </Flex>

          <main>{children ? children : <Outlet />}</main>

          <Footer />
        </ContentWrapper>
      </Wrapper>
      {sidebarOpen ? (
        <AlphaBackgroundLayer onClick={() => toggleSidebar()} />
      ) : null}
    </>
  );
}
